import { useState } from "react";
import { DateRange } from "react-day-picker";
import { isValid } from "date-fns";
import {
  ArchiveSortOptions,
  TermFacetEntry,
  AlphabeticalFacetEntry,
} from "frontend/contentful/schema/semantics";
import { ReadonlyURLSearchParams, useSearchParams } from "next/navigation";

export type Facets = Record<string, Array<string>>;

export const archiveSortsLegacy: Record<ArchiveSortOptions, string> = {
  "A — Z": "HBSSortString:asc",
  "Z — A": "HBSSortString:desc",
  Oldest: "HBSSortDate:asc",
  Newest: "HBSSortDate:desc",
};

export const archiveSorts: Record<ArchiveSortOptions, string> = {
  "A — Z": "sortString:asc",
  "Z — A": "sortString:desc",
  Oldest: "sortDate:asc",
  Newest: "sortDate:desc",
};
export const reverseSorts: Record<string, ArchiveSortOptions> =
  Object.fromEntries(
    Object.entries(archiveSorts).map(([key, value]) => [
      value,
      key as ArchiveSortOptions,
    ]),
  );

export function getInitialSelectedFacets(
  searchParams: ReadonlyURLSearchParams,
  facetEntries: Array<TermFacetEntry | AlphabeticalFacetEntry>,
  getKey?: (key: string) => string,
) {
  const initialSelectedFacets: Facets = {};

  facetEntries.forEach((facetEntry) => {
    const field = facetEntry.fields.field;
    if (!field) return;

    const values = searchParams.getAll(getKey ? getKey(field) : field);
    if (values.length > 0) {
      initialSelectedFacets[field] = values;
    }
  });

  return initialSelectedFacets;
}
export function getInitialSelectedSorts(
  searchParams: ReadonlyURLSearchParams,
  sortOptions: Array<ArchiveSortOptions>,
) {
  const initialSelectedSorts: Facets = {};

  const sortValue = searchParams.get("sort");
  if (sortValue) {
    const sortOption = reverseSorts[sortValue];
    if (sortOption !== undefined && sortOptions.includes(sortOption)) {
      initialSelectedSorts.sort = [sortValue];
    }
  }

  return initialSelectedSorts;
}

export const useDateRange = (
  getKey: (key: string) => string = (key: string) => key,
) => {
  const searchParams = useSearchParams();
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const fromParam = searchParams.get(getKey("from"));
    const fromDate =
      fromParam && !isNaN(Number(fromParam))
        ? new Date(Number(fromParam))
        : undefined;
    const toParam = searchParams.get(getKey("to"));
    const toDate =
      toParam && !isNaN(Number(toParam))
        ? new Date(Number(toParam))
        : undefined;

    return {
      from: fromDate && isValid(fromDate) ? fromDate : undefined,
      to: toDate && isValid(toDate) ? toDate : undefined,
    };
  });

  const updateDateRangeUrlParams = (newDateRange: DateRange) => {
    const url = new URL(window.location.href);
    if (newDateRange?.from) {
      url.searchParams.set(
        getKey("from"),
        newDateRange.from.getTime().toString(),
      );
    } else {
      url.searchParams.delete(getKey("from"));
    }
    if (newDateRange?.to) {
      url.searchParams.set(getKey("to"), newDateRange.to.getTime().toString());
    } else {
      url.searchParams.delete(getKey("to"));
    }
    window.history.pushState({}, "", url);
  };

  const handleDatePickerSubmit = (newDateRange: DateRange) => {
    setDateRange(newDateRange);

    updateDateRangeUrlParams(newDateRange);
  };

  const getEsDateFilters = (filters: string) => {
    if (dateRange.from || dateRange.to) {
      const prefix = filters ? " AND " : "";
      const fromEpoch = dateRange.from?.getTime();
      const toEpoch = dateRange.to?.getTime();

      if (fromEpoch && toEpoch) {
        return `${prefix}sortDate:[${fromEpoch} TO ${toEpoch}]`;
      } else if (fromEpoch) {
        return `${prefix}sortDate:>=${fromEpoch}`;
      } else if (toEpoch) {
        return `${prefix}sortDate:<=${toEpoch}`;
      }
    }
  };

  return {
    dateRange,
    resetDateRange: () => {
      setDateRange({ from: undefined, to: undefined });
      updateDateRangeUrlParams({ from: undefined, to: undefined });
    },
    handleDatePickerSubmit,
    getEsDateFilters,
  };
};
